import React, { FC, useEffect, useMemo, useCallback, useState } from "react";
import { subWeeks } from "date-fns";

import { CreateStripeSession } from "api";
import { useAccessToken } from "auth/utils/useAccessToken";
import { useAcceptTermsMutation, useGetAppOverviewQuery, useTermsOfUseAcceptanceQuery } from "generated/graphql";
import { getApiUrl } from "utils/getApiUrl";
import { wrapUrqlMutation } from "utils/graphql";
import { getProducts } from "utils/getProducts";
import { getCoupon } from "utils/getCoupons";

import { CheckoutStep, CheckoutStepProps, TermsOfService } from "../components/CheckoutStep";
import { OnboardingProps } from "../models";

export interface CheckoutStepContainerProps
    extends Omit<
        CheckoutStepProps,
        "loading" | "user" | "onAcceptTermsOfService" | "onCheckout" | "app" | "createProps"
    > {
    /**
     * Detected CMS
     */
    cms?: string;
    /**
     * The partner
     */
    partner?: string;
    /**
     * The coupon id
     */
    couponId?: string;
    /**
     * The product Id
     */
    productId?: string;
    /**
     * Optional path to redirect to on successful checkout
     */
    successPath?: string;
    /**
     * App information
     */
    app: OnboardingProps;
    /**
     * Disables checkout, only provides terms acceptance
     */
    disableCheckout?: boolean;
}

export const CheckoutStepContainer: FC<CheckoutStepContainerProps> = (props) => {
    const { app, cms, partner, productId, couponId, noTrial, disableCheckout } = props;

    const { appId, organizationId } = app;

    const [termsOfService, setTermsOfService] = useState<TermsOfService>();
    const [loadingMessage, setLoadingMessage] = useState<string>();
    const [error, setError] = useState<{ error: string; message: string }>();
    const accessToken = useAccessToken();
    const api = getApiUrl();

    const product = getProducts().find((p) => p.id === productId);

    const coupon = getCoupon(couponId);

    let successPath = props.successPath;
    if (!successPath) {
        successPath = `/${organizationId}/${appId}?checkout=success`;
        if (cms) {
            successPath += `&cms=${cms}`;
        }

        if (partner) {
            successPath += `&partner=${partner}`;
        }
    }

    const acceptTerms = wrapUrqlMutation(useAcceptTermsMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean; message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({
                error,
                message: "We're sorry, we had trouble with finalizing the terms acceptance, please try again.",
            });
            return { send: false };
        },
    });

    // get Org to see if they already agreed to the terms
    const [termsOfUseAcceptance] = useTermsOfUseAcceptanceQuery({
        pause: !organizationId,
        variables: {
            organizationId,
        },
    });

    const { data: termsOfUseData, fetching: fetchingTermsOfUseData } = termsOfUseAcceptance;

    useEffect(() => {
        if (fetchingTermsOfUseData) {
            setLoadingMessage("Checking for terms of service acceptance...");
        }

        if (termsOfUseData?.org.acceptedLatestTermsOfService) {
            const terms = termsOfUseData.org.acceptedTermsOfService[0];

            setTermsOfService({
                accepted: true,
                content: { rendered: `Terms accepted from ${terms.url}` },
                link: terms.url,
                modified_gmt: terms.acceptedOn,
            });
            setLoadingMessage("");
        }
    }, [termsOfUseData, fetchingTermsOfUseData]);

    useEffect(() => {
        async function fetchTermsOfService() {
            setLoadingMessage("Preparing terms of service for acceptance...");
            fetch("https://xapp.ai/wp-json/wp/v2/pages?slug=terms-and-conditions-monthly")
                .then<TermsOfService[]>((res) => {
                    return res.json();
                })
                .then((json) => {
                    const terms = json[0];
                    setTermsOfService(terms);
                })
                .catch((e) => {
                    console.error(e);
                });
            setLoadingMessage("");
        }

        if (!termsOfService && !fetchingTermsOfUseData && !termsOfUseData?.org.acceptedLatestTermsOfService) {
            fetchTermsOfService();
        }
    }, [
        setTermsOfService,
        termsOfService,
        setLoadingMessage,
        fetchingTermsOfUseData,
        termsOfUseData?.org.acceptedLatestTermsOfService,
    ]);

    const now = useMemo(() => new Date(), []);
    const [start] = useState<Date>(subWeeks(now, 1));
    const [end] = useState<Date>(now);

    const [getAppOverview] = useGetAppOverviewQuery({
        pause: !appId,
        requestPolicy: "cache-and-network",
        variables: {
            appId,
            end,
            start,
        },
    });

    const { data, fetching } = getAppOverview;
    const name: string = data?.app?.name;

    useEffect(() => {
        if (fetching) {
            setLoadingMessage("Retreiving necessary info...");
        } else {
            setLoadingMessage("");
        }
    }, [fetching]);

    const handleCheckout = useCallback(async () => {
        const session: CreateStripeSession = {
            appId,
            organizationId,
            prodId: productId,
            cancelPath: window.location.pathname + window.location.search,
            successPath,
        };

        if (couponId && couponId !== "null") {
            session.couponId = couponId;
        }

        if (noTrial) {
            session.noTrial = "true";
        }

        setLoadingMessage("Preparing final checkout...");

        // TODO: Update this to use getCheckoutUrl from "api"
        const url: string = await fetch(`${api}/stripe/checkout`, {
            method: "POST",
            headers: {
                "x-xapp-usertoken": accessToken,
                "content-type": "application/json",
            },
            body: JSON.stringify(session),
        })
            .then((res) => {
                return res.json();
            })
            .then((json) => {
                if (json.httpStatus === 200) {
                    return json.url;
                } else {
                    setError({ error: json.message, message: "We had trouble preparing checkout for you." });
                    return undefined;
                }
            })
            .catch((err: Error) => {
                console.error(err);
                setError({ error: err.message, message: "We had trouble preparing checkout." });
                return undefined;
            });
        if (url) {
            // Is this ok to do?
            window.location.replace(url);
        } else {
            setLoadingMessage("");
        }
    }, [accessToken, api, appId, couponId, noTrial, organizationId, productId, successPath]);

    const handleTermsOfServiceAcceptance = useCallback(async () => {
        setLoadingMessage(`Processing acceptance...`);
        await acceptTerms({ organizationId });

        // If we are disablingChecking, we are good to go
        if (disableCheckout) {
            // redirect to appId & organizationId, with CMS
            window.location.replace(successPath);
        } else {
            const acceptedTerms: TermsOfService = {
                ...termsOfService,
                accepted: true,
            };
            setTermsOfService(acceptedTerms);
            setLoadingMessage("");
        }
    }, [acceptTerms, disableCheckout, organizationId, successPath, termsOfService]);

    return (
        <CheckoutStep
            {...props}
            app={{ appId, organizationId, name }}
            createProps={app}
            product={product}
            coupon={coupon}
            error={error}
            loading={!termsOfService || !!loadingMessage || fetching}
            loadingMessage={loadingMessage}
            termsOfService={termsOfService}
            onAcceptTermsOfService={handleTermsOfServiceAcceptance}
            onCheckout={handleCheckout}
        />
    );
};

// test url http://localhost:3000/onboarding?step=checkout&website=https%3A%2F%2Fxapp.ai&appId=xapp-ai-toshx&organizationId=xapp-ai-3kzms&product=prod_ONRits4m0u02uZ&coupon=RBaohfOt
